import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import {
  DetailColumn,
  DetailField,
  DetailFieldSection,
  DetailAdditionalSection,
} from "@/components/details";
import { agreementDetail, contractorDetail } from "@/schemas/agreement";
import { useAgreementData } from "../../hooks";
import TaxStatus from "@/components/TaxStatus";
import { Module, PATH_CONTRACTOR } from "@/constants";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { EntityListLink } from "@/components/EntityListLink";
import { useFormatters } from "@/lib/hooks/useFormatters";

export const AgreementDetailsSection = () => {
  const { t } = useTranslation();
  const { formatBoolean } = useFormatters();
  const { agreement, contractor } = useAgreementData();

  return (
    <CollapsibleSection title={t("agreements.details.title")}>
      <DetailFieldSection
        module={Module.Agreements}
        schema={agreementDetail}
        data={agreement}
      >
        <DetailColumn>
          <DetailField field="agreement_ref_number" />
          <DetailField
            field="agreement_type_name"
            tooltip={t("agreements.fields.agreement_type_id_tooltip", {
              id: agreement.agreement_type_id,
            })}
          />
          <DetailField
            field="agreement_company_name"
            tooltip={t("agreements.fields.company_id_tooltip", {
              id: agreement.agreement_company_id,
            })}
          />
          <DetailField field="agreement_company_full_name" />
          <DetailField
            field="agreement_status"
            value={t(`agreementStatuses.${agreement.agreement_status}`)}
          />
          <DetailField field="is_b2b_agreement" />
          <DetailField
            field="agreement_tax_status"
            value={
              <TaxStatus
                className="text-xs"
                value={agreement.agreement_tax_status}
              />
            }
          />
          <DetailField field="recruitment_responsible_user_full_name" />
          <DetailField field="recruitment_responsible_user_role" />
        </DetailColumn>

        <DetailColumn>
          <DetailField field="agreement_start_date" />
          <DetailField field="agreement_sign_date" />
          <DetailField field="agreement_accepted_by_contractor_at" />
          <DetailField field="agreement_statements_confirmed_by_contractor_at" />
          <DetailField field="agreement_end_date" />
          <DetailField field="agreement_last_amendment_number" />
          <DetailField field="agreement_last_amendment_date" />
          <DetailField
            field="agreement_statements_up_to_date"
            value={formatBoolean(
              agreement.agreement_statements_up_to_date,
              "agreements.fields.statements_up_to_date",
            )}
          />
        </DetailColumn>
      </DetailFieldSection>

      <Separator className="my-4" />

      <DetailAdditionalSection
        title={t("contractors.details.title")}
        headerAction={
          <EntityListLink
            path={generatePath(PATH_CONTRACTOR, {
              contractorId: contractor.contractor_id,
            })}
            className="h-7 w-7 shrink-0"
          />
        }
      >
        <DetailFieldSection
          module={Module.Contractors}
          schema={contractorDetail}
          data={contractor}
        >
          <DetailColumn>
            <DetailField field="contractor_full_name" />
            <DetailField field="contractor_email" />
          </DetailColumn>
          <DetailColumn>
            <DetailField
              field="contractor_user_ref_number"
              tooltip={t("agreements.fields.contractor_id_tooltip", {
                id: contractor.contractor_id,
              })}
            />
            <DetailField field="contractor_phone_number" />
          </DetailColumn>
        </DetailFieldSection>
      </DetailAdditionalSection>
    </CollapsibleSection>
  );
};
